import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  SearchMessagesQuery,
  SearchMessagesData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface SearchMessagesState
  extends MergeRequestState<SearchMessagesData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.searchMessages,
  isFetching: (state: ReduxState) => state.server.searchMessages.isFetching,
  data: (state: ReduxState) => state.server.searchMessages.data,
  error: (state: ReduxState) => state.server.searchMessages.error,
};

const initialState: SearchMessagesState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/searchMessages';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    { query, config }: { query: SearchMessagesQuery; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.searchMessages(query, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerSearchMessages {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const searchMessages: ServerSearchMessages = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
